const NEWS_PUBLICATION_KIND_LABEL = {
    GLOBAL: "Global",
    GREMIUM: "Gremium",
    EVENT: "Veranstaltung",
    RESSORT: "Ressort",
};

function primaryNewsPublicationContext(item) {
    const list = item.publicationContexts;
    if (!list?.length) return null;
    return list.find((c) => c.isPrimary) ?? list[0];
}

function newsSlugForContext(item, ctx) {
    return ctx?.newsRouteSlug || item.route_slug || "";
}

function newsUrlFromPublicationContext(item) {
    const slug = newsSlugForContext(item, primaryNewsPublicationContext(item));
    if (!slug) return "/news";
    const p = primaryNewsPublicationContext(item);
    if (!p) return `/news/${slug}`;
    if (p.kind === "EVENT" && p.eventRouteSlug) {
        return `/event/${p.eventRouteSlug}/news/${slug}`;
    }
    if (p.kind === "GREMIUM") {
        const g = p.gremiumRouteSlug || p.gremiumLinkSegment;
        if (g) return `/gremium/${g}/news/${slug}`;
    }
    return `/news/${slug}`;
}

function gremiumPublicationDisplayName(ctx) {
    const short = (ctx.shortLabel || "").trim();
    const full = (ctx.displayName || "").trim();
    if (short && full) {
        const f = full.toLowerCase();
        const s = short.toLowerCase();
        if (f === s || f.startsWith(s + " ")) {
            return full;
        }
        return `${short} ${full}`;
    }
    return full || short;
}

function newsPublicationDisplayLine(ctx) {
    const kindLabel = NEWS_PUBLICATION_KIND_LABEL[ctx.kind] || ctx.kind;
    const name =
        ctx.kind === "GREMIUM"
            ? gremiumPublicationDisplayName(ctx)
            : ctx.displayName;
    if (ctx.kind === "GLOBAL") {
        const n = (name || "").trim();
        if (!n || n.toLowerCase() === "global") {
            return kindLabel;
        }
    }
    return `${kindLabel}: ${name}`;
}

function sortNewsPublicationContexts(contexts) {
    const kindOrder = { GLOBAL: 0, GREMIUM: 1, EVENT: 2, RESSORT: 3 };
    return [...contexts].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return (kindOrder[a.kind] ?? 9) - (kindOrder[b.kind] ?? 9);
    });
}

const typenameMapping = {
    Gremium: {
        icon: "fa-light fa-screen-users",
        title: (item) => item.shortened_type + item.name,
        url: (item) => `/gremium/${item.route_slug}`,
        date: (item) => item.created_at,
    },
    News: {
        icon: "fa-light fa-comment",
        title: (item) => item.title,
        url: (item) => newsUrlFromPublicationContext(item),
        date: (item) => item.created_at,
        items: (item) => {
            const ctxs = item.publicationContexts;
            if (!ctxs?.length) {
                return [
                    {
                        icon: "fa-light fa-globe",
                        title: "Herkunft",
                        value: "Global (DSAGNet)",
                    },
                ];
            }
            const value = sortNewsPublicationContexts(ctxs)
                .map(newsPublicationDisplayLine)
                .join(" · ");
            return [
                {
                    icon: "fa-light fa-location-dot",
                    title: "Herkunft",
                    value,
                },
            ];
        },
    },
    User: {
        icon: "fa-light fa-user",
        title: (item) => {
            let suffix = item.company ? ` (${item.company.name1})` : "";

            return `${item.first_name} ${item.last_name}${suffix}`;
        },
        url: (item) => `/benutzer/${item.id}`,
        date: () => "",
        items: (item) => [
            { icon: "fa-light fa-buildings", title: "Firma", value: item.company ? `${item.company.name1} ${item.company.name2 || ""}`.trim() : "(Gast)" },
        ],
        shortTitle: (item) => `${item.first_name} ${item.last_name}`
    },
    TopicComment: {
        icon: "fa-light fa-poll-people",
        title: (item) => `${item.user.first_name} ${item.user.last_name}`,
        content: (item) => item.content.text_content,
        url: (item) =>
            `/gremium/${item.topic.forum.gremium_route_slug}/forum/${item.topic.route_slug}/comment/${item.id}`,
        date: (item) => item.created_at,
    },
    File: {
        icon: "fa-light fa-file",
        title: (item) => item.name,
        url: (item) => `/datei/${item.id}`,
        date: (item) => item.created_at,
    },
    Company: {
        icon: "fa-light fa-buildings",
        title: (item) => `${item.name1} ${item.name2 || ''}`.trim(),
        url: (item) => `/firma/${item.id}`,
        date: (item) => item.created_at,
        items: (item) => [
            { icon: "fa-light fa-brain-circuit", title: "Branche", value: item.field?.name },
            { icon: "fa-light fa-hashtag", title: "Mitgliedsnummer", value: item.membership?.number ? String(item.membership?.number) : null },
        ],
    },
    DsagEvent: {
        icon: "fa-light fa-calendar",
        title: (item) => item.name,
        url: (item) => `/event/${item.route_slug}`,
        date: (item) => item.start,
    },
    ForumTopic: {
        icon: "fa-light fa-calendar",
        title: (item) => item.title,
        url: (item) =>
            `/gremium/${item.forum.gremium_route_slug}/forum/${item.route_slug}`,
        date: (item) => item.created_at,
    },
    CompanyPartnerProfile: {
        icon: "fa-light fa-buildings",
        title: (item) => typenameMapping.Company.title(item.comp) + " (Partner)",
        url: (item) => `/partner/${item.id}`,
        date: (item) => item.created_at,
        items: (item) => typenameMapping.Company.items(item.comp)
    },
};

const COMPANY_SEARCH_DATE_TYPES = new Set(["Company", "CompanyPartnerProfile"]);

export function isCompanySearchDateHidden(permissions) {
    return !permissions?.["admin.show"];
}

export function shouldHideSearchResultDate(typename, permissions) {
    return (
        typename === "User" ||
        (COMPANY_SEARCH_DATE_TYPES.has(typename) &&
            isCompanySearchDateHidden(permissions))
    );
}

export default typenameMapping;